import React from 'react'
import { useTranslation } from 'react-i18next'
import { Diamond, Folder, PanelRightClose } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useGanttStore } from '../store/ganttStore'
import type { ScheduleActivity, ScheduleCalendar, ScheduleDetail, ScheduleExternalActivity } from '../types'
import HealthBadge from '../components/HealthBadge'
import DatesSection from './panel/DatesSection'
import DependenciesSection from './panel/DependenciesSection'
import GeneralSection from './panel/GeneralSection'
import HistorySection from './panel/HistorySection'
import ProgressSection from './panel/ProgressSection'

interface Props {
  detail: ScheduleDetail
  byId: Map<string, ScheduleActivity>
  externalById: Map<string, ScheduleExternalActivity>
  calendarsById: Map<string, ScheduleCalendar>
  canEdit: boolean
  projectId: string
  scheduleId: string
  onOpenLog: (activityId: string) => void
}

/**
 * The right-hand activity panel (Task 17, spec B §9.7): mounted by `detail/index.tsx` next to
 * `GanttView` when the store's `panelOpen` is true. Reads `focusedId` off the gantt store itself
 * (it's rendered as a normal descendant of `GanttStoreContext.Provider`, unlike `detail/index.tsx`,
 * which creates that provider and so can't consume it) and composes the five read/write sections
 * against whichever activity is focused, each backed by `GanttOpsContext` for mutations.
 */
const ActivityPanel: React.FC<Props> = ({ detail, byId, externalById, calendarsById, canEdit, projectId, scheduleId, onOpenLog }) => {
  const { t } = useTranslation()
  const focusedId = useGanttStore((s) => s.focusedId)
  const setPanelOpen = useGanttStore((s) => s.setPanelOpen)
  const activity = focusedId ? byId.get(focusedId) : undefined

  return (
    // width mirrors `PANEL_WIDTH` (lib/constants.ts) — kept literal since Tailwind's JIT needs the
    // arbitrary-value class as source text, not assembled from the constant at runtime.
    <aside className="w-[360px] shrink-0 border-l bg-background overflow-y-auto">
      <div className="flex items-center justify-between gap-2 p-3 border-b">
        {activity ? (
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">{activity.wbsCode}</div>
            <div className="text-sm font-semibold flex items-center gap-1.5">
              {activity.isMilestone && <Diamond className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
              {activity.isSummary && <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
              <span className="truncate">{activity.name}</span>
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">{t('Schedule_panel_empty')}</span>
        )}
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setPanelOpen(false)} aria-label={t('Common_close')}>
          <PanelRightClose className="h-4 w-4" />
        </Button>
      </div>

      {activity && (
        <>
          <div className="flex items-center gap-1.5 px-3 pb-3 border-b">
            <HealthBadge health={activity.health} />
            {activity.isCritical && <Badge variant="danger">{t('Schedule_critical')}</Badge>}
          </div>

          <div className="p-3 space-y-4">
            <section>
              <h4 className="text-sm font-semibold mb-1">{t('Schedule_panel_general')}</h4>
              {/* key={activity.id}: remounts on focus change so pendingField/fieldError (and every
                  other local field) reset for free instead of leaking across activities (fix round 1,
                  review finding #3). */}
              <GeneralSection key={activity.id} activity={activity} calendarsById={calendarsById} scheduleId={scheduleId} canEdit={canEdit} />
            </section>

            {!activity.isSummary && (
              <>
                <Separator />
                <section>
                  <h4 className="text-sm font-semibold mb-1">{t('Schedule_panel_progress')}</h4>
                  <ProgressSection key={activity.id} activity={activity} scheduleId={scheduleId} canEdit={canEdit} />
                </section>
              </>
            )}

            <Separator />
            <section>
              <h4 className="text-sm font-semibold mb-1">{t('Schedule_panel_dates')}</h4>
              <DatesSection activity={activity} calendarsById={calendarsById} defaultCalendarId={detail.schedule.defaultCalendarId} />
            </section>

            <Separator />
            <section>
              <h4 className="text-sm font-semibold mb-1">{t('Schedule_panel_dependencies')}</h4>
              <DependenciesSection
                activity={activity} byId={byId} externalById={externalById} dependencies={detail.dependencies}
                scheduleName={detail.schedule.name} projectId={projectId} scheduleId={scheduleId} canEdit={canEdit}
              />
            </section>

            <Separator />
            <section>
              <h4 className="text-sm font-semibold mb-1">{t('Schedule_panel_history')}</h4>
              <HistorySection scheduleId={scheduleId} activityId={activity.id} onShowAll={() => onOpenLog(activity.id)} />
            </section>
          </div>
        </>
      )}
    </aside>
  )
}

export default ActivityPanel
